import React, { useEffect, useMemo, useState } from 'react';
import { Newspaper, Loader2 } from 'lucide-react';
import api from '../lib/api';
import { useLang, tx } from '../i18n/LanguageContext';
import { useAuth } from '../context/AuthContext';
import { useCategories } from '../hooks/useCategories';
import { useLibraryList } from '../hooks/useLibraryList';
import { hasAccess } from '../components/LockedDialog';
import { PageHero } from '../components/articles/ArticlesHero';
import { ArticleCategoryTiles } from '../components/articles/ArticleCategoryTiles';
import { ArticleCard } from '../components/articles/ArticleCard';
import { ArticlesExtras } from '../components/articles/ArticlesSidebar';
import { LibraryToolbar } from '../components/library/LibraryToolbar';
import { LibraryPagination } from '../components/library/LibraryPagination';
import { LibraryCta, ListHeading } from '../components/library/LibraryCta';
import { normalizeApiPost, ARTICLES_HERO_IMG } from '../components/articles/articleUtils';

export default function Articles() {
  const { lang } = useLang();
  const L = (ar, en) => tx(lang, ar, en);
  const { user } = useAuth();
  const { categories, getLabel } = useCategories();
  const [posts, setPosts] = useState([]);
  const [loading, setLoading] = useState(true);
  const list = useLibraryList(posts);
  const canAccess = hasAccess(user);

  useEffect(() => {
    api.get('/posts')
      .then(({ data }) => setPosts((data || []).map(normalizeApiPost)))
      .catch(() => setPosts([]))
      .finally(() => setLoading(false));
  }, []);

  const mostRead = useMemo(() => posts.filter(p => p.views > 0).sort((a, b) => b.views - a.views).slice(0, 5), [posts]);

  return (
    <main data-testid="articles-page" className="page-y min-h-screen">
      <div className="site-container">
        <PageHero
          testId="articles-hero" image={ARTICLES_HERO_IMG} BadgeIcon={Newspaper}
          badge={L('مكتبة المقالات', 'Articles library')}
          title={L('مكتبة المقالات', 'Articles Library')}
          highlight={L('معرفة عملية', 'Practical knowledge')} rest={L('لكل مهتم بالأتمتة', 'for every automation enthusiast')}
          desc={L('مقالات وشروحات تقنية مبسطة تساعدك على فهم الأتمتة وتطبيقها في حياتك العملية.', 'Simplified technical articles and guides that help you understand automation and apply it in your work.')} />
        <LibraryToolbar list={list} categories={categories} placeholder={L('ابحث عن مقال...', 'Search for an article...')} testPrefix="articles" />
        <ArticleCategoryTiles categories={categories} posts={posts} active={list.cat} onChange={list.setCat} />
        <ListHeading Icon={Newspaper} title={L('جميع المقالات', 'All articles')} list={list} unit={{ ar: 'مقال', en: 'articles' }} testId="articles-count" />
        {loading ? (
          <div className="flex justify-center py-16"><Loader2 className="w-8 h-8 animate-spin text-blue-600" /></div>
        ) : list.filtered.length === 0 ? (
          <div data-testid="articles-empty" className="text-center text-slate-500 py-16 bg-white rounded-2xl border border-dashed border-slate-200">{L('لا توجد مقالات مطابقة', 'No matching articles')}</div>
        ) : (
          <>
            <div data-testid="articles-grid" className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
              {list.visible.map((p, i) => (
                <ArticleCard key={p.id} post={p} index={i} categories={categories} getLabel={getLabel} locked={!canAccess && !p.is_free} />
              ))}
            </div>
            <LibraryPagination list={list} testPrefix="articles" />
          </>
        )}
        <ArticlesExtras mostRead={mostRead} />
        <LibraryCta />
      </div>
    </main>
  );
}
